import { useState, type ReactNode } from "react";
import { Navigate } from "react-router";
import { useMe, useRefreshTimer } from "@/shared/lib/auth";
import { useNotifications } from "@/shared/lib/notifications";
import { AuthLoadingOverlay } from "@/shared/components/ui/AuthLoadingOverlay";

interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Guards /app/* routes. Reads the session from /auth/me via React Query;
 * shows a full-screen pulsing-logo overlay while loading, redirects to
 * /auth/login on 401, then passes through to the protected content. The
 * redirect uses `replace` so the browser back button doesn't loop back into
 * the guard.
 *
 * The overlay is rendered OUTSIDE the loading/authed branch so the same
 * instance stays mounted across loading→authed and can fade out *over* the
 * now-mounted app (no swap/jank). It only unmounts once its exit animation
 * completes (see AuthLoadingOverlay / design.md §14).
 *
 * Once authed, also runs the proactive refresh timer (see useRefreshTimer)
 * scoped to the lifetime of the authed subtree.
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const me = useMe();
  const [overlayGone, setOverlayGone] = useState(false);
  const loading = me.isLoading;

  if (!loading && !me.data) {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <>
      {!loading && me.data ? <AuthedShell>{children}</AuthedShell> : null}
      {!overlayGone ? (
        <AuthLoadingOverlay show={loading} onExited={() => setOverlayGone(true)} />
      ) : null}
    </>
  );
}

function AuthedShell({ children }: { children: ReactNode }) {
  useRefreshTimer();
  useNotifications();
  return <>{children}</>;
}
